import React, { useEffect, useState } from "react";
import ContentSearchBar from "cms/components/content/ContentSearchBar/ContentSearchBar";
import {
  ContentFilters,
  ContentItem,
  ContentItemData,
  ContentItemOverview,
  ContentSearchValues,
  ContentSyncJob,
  ContentUpdateAction,
  DetailedContentItem,
  GroupedOption,
  SearchValue,
  SingleOption,
} from "interfaces";
import alertOutlined from "assets/AlertOutlined.svg";
import bugOutlined from "assets/BugOutlined.svg";
import likeOutlined from "assets/LikeOutlined.svg";
import plusOutlined from "assets/PlusOutlined.svg";
import stopOutlined from "assets/StopOutlined.svg";
import "./Content.css";
import { grey } from "@mui/material/colors";
import { HttpError, useList, useOne } from "@refinedev/core";
import ContentTable from "cms/components/content/ContentTable/ContentTable";
import { MultiValue } from "react-select";
import { SyncNotificationButton } from "cms/components/content/SyncNotificationButton/SyncNotificationButton";
import { useRealtimeData } from "controllers/ably/ably.controller";
import { axiosInstance } from "@refinedev/simple-rest";
import { API_URL } from "utils/constants";
import { useTitle } from "contexts/TitleContext";

const DEFAULT_SEARCH = { subjects: [], sections: [], templates: [] };
const DEFAULT_FILTERED_IDS = {
  subjects: [],
  sections: [],
  templates: [],
  special: [],
};

const Content: React.FC = () => {
  const { setTitle } = useTitle();
  useEffect(() => {
    setTitle("Content Management");
  }, []);

  const [data, setData] = useState<ContentItemOverview[]>([]);
  const [filteredData, setFilteredData] = useState<ContentItemOverview[]>([]);
  const [searchValues, setSearchValues] =
    useState<ContentSearchValues>(DEFAULT_SEARCH);
  const [detailedData, setDetailedData] = useState<
    Record<number, ContentItemData>
  >({});

  const [syncJobs, setSyncJobs] = useState<ContentSyncJob[]>([]);

  const { data: raw_table, isLoading } = useList<ContentItemOverview>({
    resource: "cms/admin/content/table",
  });

  const { data: raw_search_values } = useList({
    resource: "cms/admin/content/params",
  });

  const { data: raw_sync_jobs, refetch: refetchJobs } = useRealtimeData({
    endpoint: "cms/admin/content/jobs",
    channelName: "resources/cms/admin/content/jobs",
  });

  const table_data = raw_table?.data as ContentItemOverview[];
  const content_search =
    raw_search_values?.data as unknown as ContentSearchValues;
  const sync_job_data = raw_sync_jobs?.data as ContentSyncJob[];

  useEffect(() => {
    if (table_data) {
      setData(table_data);
      setFilteredData(table_data);
    }
  }, [table_data]);

  useEffect(() => {
    if (content_search) {
      setSearchValues(content_search);
    }
  }, [content_search]);

  function updateLastSynced(
    data: ContentItemOverview[],
    job_data: ContentSyncJob[]
  ) {
    let completed = job_data
      .filter((job) => ["completed"].includes(job.status))
      .map((c) => parseInt(c.meta.content_id + ""));
    for (const c of data) {
      if (completed.includes(c.id)) {
        c.is_outdated = false;
      }
    }
    return data;
  }

  const [indicators, setIndicators] = useState<{ [key: number]: string }>({});
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [expandedItem, setExpandedItem] = useState<number | null>(null);
  const [filteredIds, setFilteredIds] =
    useState<ContentFilters>(DEFAULT_FILTERED_IDS);

  useEffect(() => {
    if (sync_job_data) {
      setSyncJobs(sync_job_data);
      const newData = updateLastSynced(data, sync_job_data);
      setData(newData);
      handleSearchChange(filteredIds, newData);
    }
  }, [sync_job_data]);

  const handleSearchChange = (
    filteredIDs: ContentFilters,
    newData?: ContentItemOverview[]
  ) => {
    setFilteredIds(filteredIDs);

    const { subjects, sections, templates, special } = filteredIDs;
    const hasFilters =
      subjects.length > 0 ||
      sections.length > 0 ||
      templates.length > 0 ||
      special.length > 0;

    const sourceData = newData ?? data;
    const filtered = hasFilters
      ? sourceData.filter((row) => {
          const matchesSubject =
            subjects.length === 0 || subjects.includes(row.subject.id);
          const matchesSection =
            sections.length === 0 || sections.includes(row.section.id);
          const matchesTemplate =
            templates.length === 0 || templates.includes(row.template.id);
          const isOutdated = special.length === 0 || row.is_outdated;
          return (
            matchesSubject && matchesSection && matchesTemplate && isOutdated
          );
        })
      : sourceData;

    setFilteredData(filtered);
  };

  const fetchData = (page: number, itemsPerPage: number) => {
    console.log(
      `Fetching data for page ${page} with ${itemsPerPage} items per page`
    );
  };

  const fetchItem = async (row: number) => {
    try {
      const response = await axiosInstance.get(
        `${API_URL}/cms/admin/content/table/${row}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status !== 200) throw Error(response.statusText);

      console.log(JSON.stringify(response.data));
      detailedData[row] = response.data;
      setDetailedData(detailedData);
    } catch (e) {
      console.error(e);
    }
  };

  const updateContent = (
    action: ContentUpdateAction,
    item: ContentItemOverview
  ) => {
    console.log(`Updating content item with id ${item.id}`);
  };

  const handleIndicatorChange = (id: number, newIndicator: string) => {
    setData((prevData) =>
      prevData.map((item) =>
        item.id === id ? { ...item, indicator: newIndicator } : item
      )
    );
    setIndicators(newIndicator);
  };

  const handleSelectedItemsChange = (selectedItems: number[]) => {
    setSelectedItems(selectedItems);
  };

  const handleExpandedItemChange = (expandedItem: number | null) => {
    setExpandedItem(expandedItem);
  };

  const handleActionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedAction = event.target.value;

    switch (selectedAction) {
      case "bulkAssign":
        bulkAssign();
        break;
      case "bulkPrepped":
        bulkPrepped();
        break;
      case "exportTime":
        exportTime();
        break;
      default:
        break;
    }
  };

  const handleSyncStart = () => {
    refetchJobs();
  };

  const bulkAssign = () => {
    console.log("Bulk Assign action triggered");
  };

  const bulkPrepped = () => {
    console.log("Bulk Prepped action triggered");
  };

  const exportTime = () => {
    console.log("Export Time action triggered");
  };

  return (
    <>
      <div style={{ position: "fixed", top: 16, right: 16, zIndex: 1000 }}>
        <SyncNotificationButton jobs={syncJobs} />
      </div>
      <div className="cms-content">
        <div className="content-title">
          <h1>Content</h1>
        </div>
        <ContentSearchBar
          searchValues={searchValues}
          onFilterChange={handleSearchChange}
        />{" "}
        <div className="result-showing-block">
          <div className="custom-select-block">
            {selectedItems && selectedItems.length > 0 && (
              <>
                <label>{selectedItems.length} selected</label>
                <select
                  className="custom-select"
                  onChange={handleActionChange}
                  defaultValue=""
                >
                  <option value="" disabled className="disabled-option">
                    Choose Action
                  </option>
                  <option value="bulkAssign">Bulk Assign</option>
                  <option value="bulkPrepped">Bulk Prepped</option>
                  <option value="exportTime">Export Time</option>
                </select>
              </>
            )}
          </div>
        </div>{" "}
        <ContentTable
          data={filteredData}
          detailedData={detailedData}
          totalCount={filteredData.length}
          syncJobs={syncJobs}
          fetchItem={fetchItem}
          fetchData={fetchData}
          updateContent={updateContent}
          onIndicatorChange={handleIndicatorChange}
          setIndicators={setIndicators}
          onSelectedItemsChange={handleSelectedItemsChange}
          onExpandedItemChange={handleExpandedItemChange}
          onSyncStart={handleSyncStart}
        />
      </div>
    </>
  );
};

export default Content;
